"use client";
import React from 'react';
// Enhancement: Added a lightweight ZIP centering form ("Center Map by ZIP") below the main search bar.
// When a 5-digit ZIP is submitted, we push it into the `location` query param.
// The `RotationMap` component listens to the `zip` prop, performs client-side Mapbox geocoding,
// and recenters the map + drops a pin. We also filter rotations within ~60 miles using a Haversine calc.
// For production scale: consider server-side geocode proxy, caching results, and expanding radius controls.
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams, useRouter } from 'next/navigation';
import { useAuth as useClerkAuth, UserButton } from '@clerk/nextjs';
import { useAuthStore } from '@/store/auth-store';
import { PreceptorResultCard } from './preceptor-result-card';
import { RotationMap } from './maps/RotationMap';
import { DealBookingModal, type DealBookingFormData } from './deal-booking-modal';
import type { Preceptor } from '@/types/preceptor';
import { SimpleFilters } from './simple-filters';
import { geocodeZipDetailed } from '@/lib/map/geocode';
import { getAllPreceptors, type BackendPreceptor } from '@/services/preceptor-service';
import { getAllImgPreceptors } from '@/services/img-preceptor-service';
import { getPreceptorDisplayName } from '@/lib/preceptor/identity';
import { PRECEPTOR_RADIUS_MILES } from '@/utils/preceptor-radius';
import { buildSchedulingNotes } from '@/lib/preceptor/scheduling-notes';

// Fallback dummy dataset (minimal, for offline testing only)
const DUMMY_RAW: Omit<Preceptor, 'createdAt'>[] = [];

// Attach a static createdAt to satisfy required Preceptor field
const DUMMY: Preceptor[] = DUMMY_RAW.map(p => ({ ...p, createdAt: '2025-01-01T00:00:00Z' }));

const DEFAULT_PRECEPTOR_RADIUS_MILES = PRECEPTOR_RADIUS_MILES;
const NEARBY_RADIUS_MILES = 60;
const MAP_ZOOM_USA = 4;
const MAP_ZOOM_FILTERED = 12;
const MIN_BOUNDS_PADDING = 0.1; // ~7 miles padding for single-point clusters

function coerceCoordinate(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  const numeric = typeof value === 'string' ? parseFloat(value) : value;
  if (typeof numeric !== 'number' || Number.isNaN(numeric) || !Number.isFinite(numeric)) return undefined;
  return numeric;
}

const hasValidCoordinates = (p: Preceptor): p is Preceptor & { latitude: number; longitude: number } => {
  if (typeof p.latitude !== 'number' || typeof p.longitude !== 'number') return false;
  if (!Number.isFinite(p.latitude) || !Number.isFinite(p.longitude)) return false;
  return !(p.latitude === 0 && p.longitude === 0);
};

const zipMatches = (value: string | undefined | null, target: string) =>
  typeof value === 'string' && value.trim().startsWith(target);

const preceptorHasZip = (p: Preceptor, zip: string) => {
  if (zipMatches(p.zip, zip)) return true;
  if (Array.isArray(p.siteZips)) {
    return p.siteZips.some((z) => zipMatches(z, zip));
  }
  return false;
};

function transformBackendPreceptor(bp: BackendPreceptor): Preceptor {
  // Each backend row now represents a single preceptor_sites pivot (one preceptor × one site).
  // `bp.site` is that pivot's site. `bp.sites` is the full list (still present for reference).
  const site = bp.site || null;

  const actualLat = coerceCoordinate(site?.latitude);
  const actualLng = coerceCoordinate(site?.longitude);
  const publicLat = coerceCoordinate(site?.public_location?.latitude);
  const publicLng = coerceCoordinate(site?.public_location?.longitude);

  let displayLat: number | undefined;
  let displayLng: number | undefined;

  if (publicLat !== undefined && publicLng !== undefined) {
    displayLat = publicLat;
    displayLng = publicLng;
  } else if (actualLat !== undefined && actualLng !== undefined) {
    const randomized = randomPointInRadius(actualLat, actualLng, 0.17);
    displayLat = randomized.lat;
    displayLng = randomized.lng;
  }

  const seasonsList = Array.isArray(bp.seasons_available) ? bp.seasons_available : (bp.seasons_available ? [bp.seasons_available] : []);
  const siteZip = site?.zip ? String(site.zip).trim() : '';
  const siteZips = siteZip ? [siteZip] : [];

  const specialties = Array.isArray(bp.specialties) && bp.specialties.length > 0
    ? bp.specialties
        .filter((s): s is { id: number | string; name?: string } => !!s.name)
        .map(s => ({ id: s.id, name: s.name! }))
    : bp.specialty
      ? [{ id: bp.specialty.id, name: bp.specialty.name }]
      : [];

  const specialty_ids = bp.specialty_ids && bp.specialty_ids.length
    ? bp.specialty_ids.map(id => Number(id))
    : bp.specialty_id
      ? [Number(bp.specialty_id)]
      : specialties.map(s => Number(s.id));

  const primarySpecialty = specialties[0]?.name || 'General';

  // Card id = pivot id so each preceptor×site pair has a unique key across the feed.
  // Falls back to the old preceptor id for backward compatibility if the backend hasn't shipped yet.
  const preceptorSiteId = (bp as { preceptor_site_id?: number | string }).preceptor_site_id;
  const cardId = preceptorSiteId != null ? String(preceptorSiteId) : String(bp.id);

  return {
    id: cardId,
    name: getPreceptorDisplayName(bp),
    specialty: primarySpecialty,
    specialty_ids,
    specialties,
    rotationCode: bp.rotation_code || '',
    rotationSetting: bp.rotation_setting || '',
    seasonsAvailable: seasonsList,
    bio: bp.bio || '',
    latitude: displayLat,
    longitude: displayLng,
    city: site?.city || '',
    state: site?.state || '',
    zip: siteZip,
    siteZips,
    availableSlots: bp.available_slots || 0,
    createdAt: bp.created_at || new Date().toISOString(),
    accepts_tele: bp.accepts_tele,
    accepts_in_person: bp.accepts_in_person,
    schedulingNotes: buildSchedulingNotes(
      bp.seasons_unavailable,
      (bp as { restricted_universities?: unknown }).restricted_universities
    ).notes,
    titles: Array.isArray((bp as { titles?: unknown }).titles)
      ? ((bp as { titles?: unknown[] }).titles ?? []).filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
      : null,
  };
}

// Removed hardcoded ZIP coordinates. We now geocode any 5-digit ZIP dynamically via Mapbox.

// Generate a random point within a radius (in miles) from a center point
// This ensures the actual location is obscured for privacy while still being within the display radius
function randomPointInRadius(centerLat: number, centerLng: number, radiusMiles: number = 0.24): { lat: number; lng: number } {
  const radiusInDegrees = radiusMiles / 69; // Rough approximation: 1 degree ≈ 69 miles
  
  // Generate random angle and distance
  const angle = Math.random() * 2 * Math.PI;
  const distance = Math.random() * radiusInDegrees;
  
  // Calculate offset
  const deltaLat = distance * Math.cos(angle);
  const deltaLng = distance * Math.sin(angle) / Math.cos(centerLat * Math.PI / 180);
  
  return {
    lat: centerLat + deltaLat,
    lng: centerLng + deltaLng
  };
}

function haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // miles
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function boundsEqual(
  a: [number, number, number, number] | null,
  b: [number, number, number, number] | null
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.every((val, idx) => val === b[idx]);
}

// Added optional defaultZip prop so we can seed initial map center without waiting for geocode.
// This prevents a brief global map flash on first load when no location query param is present.
export const PreceptorSearchExperience: React.FC<{ baseRoute?: string; defaultZip?: string; activeTrack?: string }> = ({ baseRoute = '/preceptor/np/search', defaultZip }) => {
  const params = useSearchParams();
  const router = useRouter();
  const calendlyBookingUrl = process.env.NEXT_PUBLIC_CALENDLY_LINK;

  // Auth state for the page's custom desktop header (logo • filters • dashboard).
  const { isSignedIn, isLoaded: authLoaded } = useClerkAuth();
  const authUser = useAuthStore((state) => state.user);
  const [headerMounted, setHeaderMounted] = React.useState(false);
  React.useEffect(() => { setHeaderMounted(true); }, []);
  const getProfileLink = () => {
    if (authUser?.role === 'admin') return '/admin/dashboard';
    if (authUser?.role === 'preceptor') return '/preceptor/dashboard';
    return '/student/profile';
  };

  // Track derived from the route. IMG search pulls from the separate IMG preceptors
  // dataset; everything else (NP/PA) keeps the existing behavior.
  const isImg = baseRoute.includes('/img/');
  const trackKey = isImg ? 'img' : undefined;

  // Date range params for availability filtering
  const startDateParam = params.get('start') || undefined;
  const endDateParam = params.get('end') || undefined;

  // Fetch preceptors from backend API
  const [preceptors, setPreceptors] = React.useState<Preceptor[]>([]);
  const [isLoadingPreceptors, setIsLoadingPreceptors] = React.useState(true);
  const [preceptorError, setPreceptorError] = React.useState<string | null>(null);
  const lastFetchParams = React.useRef<string>('');

  React.useEffect(() => {
    // Build a cache key from date params to detect changes
    const fetchKey = `${startDateParam || ''}_${endDateParam || ''}`;

    // Skip if we already fetched with the same params
    if (lastFetchParams.current === fetchKey && preceptors.length > 0) {
      return;
    }

    lastFetchParams.current = fetchKey;

    const fetchPreceptors = async () => {
      try {
        setIsLoadingPreceptors(true);
        setPreceptorError(null);

        // Pass date filters to hide unavailable preceptors
        const fetchPreceptorList = isImg ? getAllImgPreceptors : getAllPreceptors;
        const preceptorList = await fetchPreceptorList({
          start_date: startDateParam,
          end_date: endDateParam,
        });
        const transformedPreceptors = preceptorList.map(transformBackendPreceptor);
        setPreceptors(transformedPreceptors);
      } catch (error) {
        console.error('Failed to fetch preceptors:', error);
        setPreceptorError(error instanceof Error ? error.message : 'Failed to fetch preceptors');
        setPreceptors(DUMMY); // Fallback to empty dummy data
      } finally {
        setIsLoadingPreceptors(false);
      }
    };

    fetchPreceptors();
  }, [startDateParam, endDateParam, preceptors.length]);

  // Use backend data if available, otherwise fallback to dummy
  const allPreceptors = preceptors.length > 0 ? preceptors : DUMMY;

  const defaultZipInfo = React.useMemo(() => {
    if (!defaultZip) return null;
    const pts = allPreceptors.filter(p => preceptorHasZip(p, defaultZip) && hasValidCoordinates(p));
    if (!pts.length) return null;
    const totals = pts.reduce(
      (acc, p) => {
        const lon = p.longitude as number;
        const lat = p.latitude as number;
        return {
          sumLon: acc.sumLon + lon,
          sumLat: acc.sumLat + lat,
          minLon: Math.min(acc.minLon, lon),
          maxLon: Math.max(acc.maxLon, lon),
          minLat: Math.min(acc.minLat, lat),
          maxLat: Math.max(acc.maxLat, lat),
        };
      },
      { sumLon: 0, sumLat: 0, minLon: Infinity, maxLon: -Infinity, minLat: Infinity, maxLat: -Infinity }
    );
    const avgLon = totals.sumLon / pts.length;
    const avgLat = totals.sumLat / pts.length;
    const bounds: [number, number, number, number] = [totals.minLon, totals.minLat, totals.maxLon, totals.maxLat];
    const first = pts[0];
    const placeName = first?.city && first?.state ? `${first.city}, ${first.state} ${defaultZip}` : `ZIP ${defaultZip}`;
    return {
      pin: [avgLon, avgLat] as [number, number],
      center: { lon: avgLon, lat: avgLat },
      bounds,
      placeName,
    };
  }, [defaultZip, allPreceptors]);

  const locationParam = (params.get('location') || '').trim();
  const specialtyIdParam = params.get('specialty_id') || '';
  const specialtyParam = params.get('specialty') || ''; // Keep for backward compatibility
  const stateParam = params.get('state') || '';
  const cityParam = params.get('city') || '';
  const radiusParam = params.get('radius');
  const userLat = params.get('userLat');
  const userLng = params.get('userLng');
  const radiusMiles = radiusParam ? Number(radiusParam) : null;
  const userCoords = userLat && userLng ? { lat: Number(userLat), lng: Number(userLng) } : null;

  const selectedSpecialtyName = React.useMemo(() => {
    if (!specialtyIdParam) return null;
    const targetId = Number(specialtyIdParam);
    if (Number.isNaN(targetId)) return null;
    for (const p of allPreceptors) {
      const matchById = p.specialties?.find(s => Number(s.id) === targetId);
      if (matchById?.name) return matchById.name;
    }
    return null;
  }, [specialtyIdParam, allPreceptors]);

  // Check if any filters are active (computed early for state initialization)
  const hasAnyFilters = !!(specialtyIdParam || specialtyParam || stateParam || cityParam || locationParam || radiusMiles);

  // Store current search URL in sessionStorage so rotation detail pages can link back with filters
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentUrl = window.location.href;
      sessionStorage.setItem('preceptorSearchUrl', currentUrl);
    }
  }, [specialtyIdParam, specialtyParam, stateParam, cityParam, locationParam, radiusMiles]);

  // Removed user geolocation (Center Me) feature per request.
  const [zipPin, setZipPin] = React.useState<[number, number] | null>(() => {
    // Don't use default zip pin when no filters are active - show full USA
    if (!hasAnyFilters && !locationParam) return null;
    if (!locationParam && defaultZipInfo) return defaultZipInfo.pin;
    return null;
  }); // Stable [lng, lat] for camera & markers
  const [zipCenter, setZipCenter] = React.useState<{ lat: number; lon: number; zip: string } | null>(() => {
    // Don't use default zip center when no filters are active - show full USA
    if (!hasAnyFilters && !locationParam) return null;
    if (!locationParam && defaultZipInfo && defaultZip) {
      return { zip: defaultZip, lat: defaultZipInfo.center.lat, lon: defaultZipInfo.center.lon };
    }
    return null;
  });
  const [zipGeoLoading, setZipGeoLoading] = React.useState(false);
  const [zipGeoError, setZipGeoError] = React.useState<string | null>(null);
  const [zipBounds, setZipBounds] = React.useState<[number, number, number, number] | null>(() => {
    // Don't use default zip bounds when no filters are active - show full USA
    if (!hasAnyFilters && !locationParam) return null;
    if (!locationParam && defaultZipInfo?.bounds) return defaultZipInfo.bounds;
    return null;
  });
  const [zipPlaceName, setZipPlaceName] = React.useState<string | null>(() => {
    // Don't show default place name when no filters are active
    if (!hasAnyFilters && !locationParam) return null;
    if (!locationParam && defaultZipInfo) return defaultZipInfo.placeName;
    if (!locationParam && defaultZip) return `ZIP ${defaultZip}`;
    return null;
  });
  // City geocoding state
  const [cityCenter, setCityCenter] = React.useState<{ lat: number; lon: number; city: string } | null>(null);
  // Map viewport filtering state
  const [mapViewport, setMapViewport] = React.useState<{ north: number; south: number; east: number; west: number } | null>(null);
  // Selection & pagination
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [visibleCount, setVisibleCount] = React.useState<number>(20); // show more baseline
  // Mobile: filters are collapsed behind a "Filter" button to save space
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  // Dynamic layout height calculation to eliminate bottom gap
  const chromeRef = React.useRef<HTMLDivElement | null>(null);
  const desktopHeaderRef = React.useRef<HTMLElement | null>(null);
  const [chromeHeight, setChromeHeight] = React.useState(0);

  // Modal state management
  const [isMessageModalOpen, setIsMessageModalOpen] = React.useState(false);
  const [selectedPreceptorForMessage, setSelectedPreceptorForMessage] = React.useState<Preceptor | null>(null);

  // State to force map re-fit when filter is re-selected
  const [fitBoundsKey, setFitBoundsKey] = React.useState(0);

  // Debounced bounds change handler
  const boundsChangeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const handleMapBoundsChange = React.useCallback((bounds: { north: number; south: number; east: number; west: number }) => {
    // Debounce updates to avoid too frequent re-renders during pan/zoom
    if (boundsChangeTimeoutRef.current) {
      clearTimeout(boundsChangeTimeoutRef.current);
    }
    boundsChangeTimeoutRef.current = setTimeout(() => {
      setMapViewport(bounds);
    }, 300); // 300ms debounce
  }, []);

  // Step 1: Filter by specialty only - this determines what shows on the map
  let specialtyFiltered = allPreceptors.filter(p => {
    // Filter by specialty - prefer ID-based filtering, fall back to name for backward compatibility
    if (specialtyIdParam) {
      const specialtyId = Number(specialtyIdParam);
      const matchesSpecialty =
        p.specialty_ids?.includes(specialtyId) ||
        p.specialties?.some(s => Number(s.id) === specialtyId) ||
        (selectedSpecialtyName
          ? (p.specialty || '').toLowerCase().trim() === selectedSpecialtyName.toLowerCase().trim() ||
            p.specialties?.some(s => s.name?.toLowerCase().trim() === selectedSpecialtyName.toLowerCase().trim())
          : false);
      if (!matchesSpecialty) return false;
    } else if (specialtyParam) {
      // Backward compatibility: fall back to name-based filtering
      const paramLower = specialtyParam.toLowerCase().trim();
      const matchesSpecialty = 
        p.specialty?.toLowerCase().trim() === paramLower || 
        p.specialties?.some(s => s.name?.toLowerCase().trim() === paramLower);
      if (!matchesSpecialty) return false;
    }
    
    return true;
  });

  // Step 2: For backwards compatibility, keep 'filtered' but it now equals specialtyFiltered
  // State/city/location filters now only control map viewport, not the list
  let filtered = specialtyFiltered;

  let centerLat: number | undefined;
  let centerLon: number | undefined;
  let locationBadgeLabel: string | null = null;
  const locationBadgeSuffix = '';
  const locationBadgeApprox = '';
  
  // Check if any filters are active
  const hasActiveFilters = hasAnyFilters;
  
  // Show default location when no location param is provided and no filters active
  const showingDefaultLocation = !hasActiveFilters;
  const defaultZipActive = !locationParam && !!defaultZipInfo;

  // If location contains a 4- or 5-digit postal code (even with extra text), treat as ZIP; otherwise treat as city/state text.
  const zipMatch = locationParam.match(/\b\d{4,5}\b/);
  const userZip = zipMatch ? zipMatch[0] : null;
  const userCity = !userZip && locationParam ? locationParam.toLowerCase() : null;
  const activeZip = userZip ?? (defaultZipActive ? defaultZip ?? null : null);
  
  // Determine which coordinates to use for map centering (state/city filters control map zoom)
  let radiusCenter: { lat: number; lng: number } | null = null;
  
  // Priority 1: User's actual location (from Nearby button)
  if (userCoords) {
    radiusCenter = userCoords;
    centerLat = userCoords.lat;
    centerLon = userCoords.lng;
  }
  // Priority 2: Geocoded ZIP center
  else if (activeZip && zipCenter && zipCenter.zip === activeZip) {
    radiusCenter = { lat: zipCenter.lat, lng: zipCenter.lon };
    centerLat = zipCenter.lat;
    centerLon = zipCenter.lon;
  }
  // Priority 3: Default ZIP info
  else if (defaultZipActive && defaultZipInfo) {
    radiusCenter = { lat: defaultZipInfo.center.lat, lng: defaultZipInfo.center.lon };
    centerLat = defaultZipInfo.center.lat;
    centerLon = defaultZipInfo.center.lon;
  }
  // Priority 4: Geocoded city center
  else if (userCity && cityCenter && cityCenter.city === locationParam.toLowerCase()) {
    radiusCenter = { lat: cityCenter.lat, lng: cityCenter.lon };
    centerLat = cityCenter.lat;
    centerLon = cityCenter.lon;
  }
  // Priority 5: State center - geocode the state
  else if (stateParam) {
    // Find preceptors in this state to calculate center
    const statePreceptors = specialtyFiltered.filter(p => 
      hasValidCoordinates(p) && p.state?.toUpperCase().trim() === stateParam.toUpperCase().trim()
    );
    if (statePreceptors.length > 0) {
      const { sumLat, sumLon } = statePreceptors.reduce(
        (acc, p) => ({ sumLat: acc.sumLat + (p.latitude as number), sumLon: acc.sumLon + (p.longitude as number) }),
        { sumLat: 0, sumLon: 0 }
      );
      centerLat = sumLat / statePreceptors.length;
      centerLon = sumLon / statePreceptors.length;
      radiusCenter = { lat: centerLat, lng: centerLon };
    }
  }
  // Priority 6: City center - find preceptors in the city
  else if (cityParam) {
    const cityPreceptors = specialtyFiltered.filter(p => 
      hasValidCoordinates(p) && p.city?.toLowerCase().trim() === cityParam.toLowerCase().trim()
    );
    if (cityPreceptors.length > 0) {
      const { sumLat, sumLon } = cityPreceptors.reduce(
        (acc, p) => ({ sumLat: acc.sumLat + (p.latitude as number), sumLon: acc.sumLon + (p.longitude as number) }),
        { sumLat: 0, sumLon: 0 }
      );
      centerLat = sumLat / cityPreceptors.length;
      centerLon = sumLon / cityPreceptors.length;
      radiusCenter = { lat: centerLat, lng: centerLon };
    }
  }

  // Apply radius filtering if radius is specified and we have a center point
  // NOTE: Radius filtering still applies to both map and list (it's an explicit user request)
  if (radiusMiles && radiusCenter) {
    filtered = filtered.filter(p => {
      if (!hasValidCoordinates(p)) return false;
      const distance = haversineMiles(radiusCenter.lat, radiusCenter.lng, p.latitude, p.longitude);
      return distance <= radiusMiles;
    });
    
    // Update location badge for radius searches
    if (userCoords) {
      locationBadgeLabel = `${radiusMiles} miles from your location`;
    } else if (activeZip) {
      const zipLabel = zipPlaceName || `ZIP ${activeZip}`;
      locationBadgeLabel = `${zipLabel} (${radiusMiles} mi radius)`;
    }
  }
  // Showing default location - show all preceptors across USA
  else if (showingDefaultLocation) {
    locationBadgeLabel = 'All USA';
    // No center - let map show full USA bounds
    centerLat = undefined;
    centerLon = undefined;
  } 
  // State filter active - set badge but don't filter the list
  else if (stateParam) {
    locationBadgeLabel = stateParam.toUpperCase();
  }
  // City filter active - set badge but don't filter the list  
  else if (cityParam) {
    locationBadgeLabel = cityParam;
  }
  // ZIP active
  else if (activeZip && !showingDefaultLocation) {
    const zipLabel = zipPlaceName || `ZIP ${activeZip}`;
    locationBadgeLabel = zipLabel;
    
    if (!centerLat && !centerLon) {
      if (zipCenter && zipCenter.zip === activeZip) {
        centerLat = zipCenter.lat;
        centerLon = zipCenter.lon;
      }
    }
  }

  // Simple in-memory cache for ZIP coordinates to prevent re-fetch loops
  // Cache versioning ensures we can invalidate older coordinate calculations when logic changes
  const GEO_ZIP_VERSION = 3;
  const zipCacheRef = React.useRef<Record<string, { lon: number; lat: number; v: number; bbox?: [number, number, number, number]; placeName?: string }>>({});

  React.useEffect(() => {
    if (!userZip) {
      if (defaultZipActive && defaultZipInfo && defaultZip) {
        const desiredPin = defaultZipInfo.pin;
        if (!zipPin || zipPin[0] !== desiredPin[0] || zipPin[1] !== desiredPin[1]) {
          setZipPin(desiredPin);
        }
        if (!zipCenter || zipCenter.zip !== defaultZip) {
          setZipCenter({ zip: defaultZip, lat: defaultZipInfo.center.lat, lon: defaultZipInfo.center.lon });
        }
        const desiredBounds = defaultZipInfo.bounds ?? null;
        if (!boundsEqual(zipBounds, desiredBounds)) {
          setZipBounds(desiredBounds);
        }
        const desiredPlace = defaultZipInfo.placeName || `ZIP ${defaultZip}`;
        if (zipPlaceName !== desiredPlace) {
          setZipPlaceName(desiredPlace);
        }
      } else {
        if (zipPin) setZipPin(null);
        if (zipCenter) setZipCenter(null);
        if (zipBounds) setZipBounds(null);
        if (zipPlaceName) setZipPlaceName(null);
      }
      setZipGeoError(null);
      setZipGeoLoading(false);
      return;
    }
    // If already centered on this zip, skip
    if (zipCenter?.zip === userZip) return;
    // If cached, use cache immediately
    const cached = zipCacheRef.current[userZip];
    if (cached && cached.v === GEO_ZIP_VERSION) {
      setZipCenter({ zip: userZip, lon: cached.lon, lat: cached.lat });
      const cachedBounds = cached.bbox || null;
      if (!boundsEqual(zipBounds, cachedBounds)) {
        setZipBounds(cachedBounds);
      }
      const place = cached.placeName || `ZIP ${userZip}`;
      if (zipPlaceName !== place) {
        setZipPlaceName(place);
      }
      // Compute stable zipPin from bbox midpoint or feature center
      const midFromBbox = cached.bbox
        ? [(cached.bbox[0] + cached.bbox[2]) / 2, (cached.bbox[1] + cached.bbox[3]) / 2] as [number, number]
        : null;
      const desiredPin = midFromBbox || [cached.lon, cached.lat];
      if (!zipPin || zipPin[0] !== desiredPin[0] || zipPin[1] !== desiredPin[1]) {
        setZipPin(desiredPin);
      }
      return;
    }
    let cancelled = false;
    setZipGeoLoading(true);
    setZipGeoError(null);
    geocodeZipDetailed(userZip).then(result => {
      if (cancelled) return;
      if (!result) {
        setZipGeoError('ZIP not found');
        setZipCenter(null);
        setZipPin(null);
        setZipBounds(null);
        setZipPlaceName(null);
      } else {
        const data = { lon: result.center[0], lat: result.center[1], v: GEO_ZIP_VERSION, bbox: result.bbox, placeName: result.place_name };
        zipCacheRef.current[userZip] = data;
        setZipCenter({ zip: userZip, lon: data.lon, lat: data.lat });

        // Calculate bounds from actual preceptor cluster in this ZIP
        const preceptorsInZip = allPreceptors.filter(p => preceptorHasZip(p, userZip) && hasValidCoordinates(p));
        let bounds = result.bbox || null;
        if (preceptorsInZip.length > 0) {
          // Compute cluster bounds from preceptors
          const clusterBounds = preceptorsInZip.reduce(
            (acc, p) => ({
              minLng: Math.min(acc.minLng, p.longitude as number),
              maxLng: Math.max(acc.maxLng, p.longitude as number),
              minLat: Math.min(acc.minLat, p.latitude as number),
              maxLat: Math.max(acc.maxLat, p.latitude as number),
            }),
            { minLng: Infinity, maxLng: -Infinity, minLat: Infinity, maxLat: -Infinity }
          );
          // Use cluster bounds instead of ZIP bounds to zoom into preceptor cluster
          bounds = [clusterBounds.minLng, clusterBounds.minLat, clusterBounds.maxLng, clusterBounds.maxLat] as [number, number, number, number];
        }
        if (!boundsEqual(zipBounds, bounds)) {
          setZipBounds(bounds);
        }
        const place = result.place_name || `ZIP ${userZip}`;
        if (zipPlaceName !== place) {
          setZipPlaceName(place);
        }
        // Compute stable zipPin from bbox midpoint or feature center (once)
        const midFromBbox = result.bbox
          ? [(result.bbox[0] + result.bbox[2]) / 2, (result.bbox[1] + result.bbox[3]) / 2] as [number, number]
          : null;
        const desiredPin = midFromBbox || (result.center as [number, number]);
        if (!zipPin || zipPin[0] !== desiredPin[0] || zipPin[1] !== desiredPin[1]) {
          setZipPin(desiredPin);
        }
      }
    }).catch(() => {
      if (cancelled) return;
      setZipGeoError('Geocode failed');
      setZipCenter(null);
      setZipPin(null);
      setZipBounds(null);
      setZipPlaceName(null);
    }).finally(() => !cancelled && setZipGeoLoading(false));
    return () => { cancelled = true; };
  }, [userZip, defaultZipActive, defaultZipInfo, defaultZip, zipCenter, zipPin, zipBounds, zipPlaceName]);

  // Geocode city names for radius search
  React.useEffect(() => {
    if (!userCity || !locationParam) {
      if (cityCenter) setCityCenter(null);
      return;
    }
    // If already geocoded this city, skip
    if (cityCenter && cityCenter.city === locationParam.toLowerCase()) return;
    
    let cancelled = false;
    // Use Mapbox geocoding to get city center
    const token = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
    if (!token) return;
    
    fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(locationParam)}.json?access_token=${token}&types=place,locality`)
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        const feature = data.features?.[0];
        if (feature?.center) {
          const [lon, lat] = feature.center;
          setCityCenter({ lat, lon, city: locationParam.toLowerCase() });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setCityCenter(null);
      });
    
    return () => { cancelled = true; };
  }, [userCity, locationParam, cityCenter]);

  // Measure the visible header/controls height to size the columns to the remaining
  // viewport. The desktop header and mobile controls are separate elements (one is
  // display:none per breakpoint, giving offsetHeight 0), so we take the larger one.
  React.useEffect(() => {
    const measure = () => {
      const headerH = desktopHeaderRef.current?.offsetHeight ?? 0;
      const mobileH = chromeRef.current?.offsetHeight ?? 0;
      setChromeHeight(Math.max(headerH, mobileH));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    if (desktopHeaderRef.current) observer.observe(desktopHeaderRef.current);
    if (chromeRef.current) observer.observe(chromeRef.current);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  function handleFilterChange(specialtyId: string, state: string, city: string) {
    const sp = new URLSearchParams();
    if (specialtyId) sp.set('specialty_id', specialtyId);
    if (state) {
      sp.set('state', state);
      sp.set('location', state);
    }
    if (city) sp.set('city', city);
    
    // Force map to re-fit bounds when filter is selected (even if same value)
    setFitBoundsKey(k => k + 1);
    
    // Build the URL - if no params, just use baseRoute without query string
    const queryString = sp.toString();
    const newUrl = queryString ? `${baseRoute}?${queryString}` : baseRoute;
    router.push(newUrl);
  }

  // NOTE: ZIP submission now handled only through main Location field in `PreceptorSearchBar`.

  // Subtract a bit extra so the cards list and map don't sit flush against the bottom edge.
  const columnHeight = `calc(100vh - ${chromeHeight}px - 2rem)`;

  // For the map, show ALL filtered preceptors with valid coordinates (not limited by visibleCount)
  // This ensures the map displays and fits all matching preceptors
  const mapPreceptors = filtered.filter(hasValidCoordinates);
  
  // For the list, filter by current map viewport bounds
  // This syncs the list with what's visible on the map
  const viewportFiltered = React.useMemo(() => {
    if (!mapViewport) return filtered;
    
    return filtered.filter(p => {
      if (!hasValidCoordinates(p)) return false;
      return (
        p.latitude >= mapViewport.south &&
        p.latitude <= mapViewport.north &&
        p.longitude >= mapViewport.west &&
        p.longitude <= mapViewport.east
      );
    });
  }, [filtered, mapViewport]);
  
  // Apply pagination to viewport-filtered results
  const listPreceptors = viewportFiltered.slice(0, visibleCount);
  
  // Update location badge when viewport filtering is active
  const viewportBadgeLabel = mapViewport && !showingDefaultLocation 
    ? `${viewportFiltered.length} in view` 
    : null;

  // Initialize bounds - only use zipBounds if we have an active ZIP search
  let computedZipBounds = activeZip ? (zipBounds || undefined) : undefined;
  
  // Determine which preceptors to use for bounds calculation
  // When state/city filter is active, zoom to those preceptors
  // Check city first since it's more specific than state
  let boundsPreceptors = mapPreceptors;
  if (cityParam) {
    // City filter - filter by both state and city for accuracy
    const cityLower = cityParam.toLowerCase().trim();
    boundsPreceptors = mapPreceptors.filter(p => p.city?.toLowerCase().trim() === cityLower);
    // If state is also set, further filter by state
    if (stateParam) {
      const stateUpper = stateParam.toUpperCase().trim();
      boundsPreceptors = boundsPreceptors.filter(p => p.state?.toUpperCase().trim() === stateUpper);
    }
  } else if (stateParam) {
    const stateUpper = stateParam.toUpperCase().trim();
    boundsPreceptors = mapPreceptors.filter(p => p.state?.toUpperCase().trim() === stateUpper);
  }
  
  // Extract to named boolean for better readability
  const shouldCalculateBoundsFromFilters = 
    boundsPreceptors.length > 0 &&
    !radiusMiles && 
    !activeZip;
  
  // Always calculate bounds to fit preceptors for the current filter
  if (shouldCalculateBoundsFromFilters) {
    const cluster = boundsPreceptors.reduce(
      (acc, p) => ({
        minLng: Math.min(acc.minLng, p.longitude as number),
        maxLng: Math.max(acc.maxLng, p.longitude as number),
        minLat: Math.min(acc.minLat, p.latitude as number),
        maxLat: Math.max(acc.maxLat, p.latitude as number),
      }),
      { minLng: Infinity, maxLng: -Infinity, minLat: Infinity, maxLat: -Infinity }
    );
    
    // Add margin to prevent markers from being at the very edge
    // Use tighter margin for city, medium for state, wider for no filter
    // 0.02 degrees ≈ 1.4 miles for city (tight), 0.3 for state, 0.5 for no filter
    const MARGIN = cityParam ? 0.02 : (stateParam ? 0.3 : 0.5);
    const isSinglePoint = cluster.minLng === cluster.maxLng && cluster.minLat === cluster.maxLat;
    
    if (isSinglePoint) {
      computedZipBounds = [
        cluster.minLng - MIN_BOUNDS_PADDING,
        cluster.minLat - MIN_BOUNDS_PADDING,
        cluster.maxLng + MIN_BOUNDS_PADDING,
        cluster.maxLat + MIN_BOUNDS_PADDING
      ] as [number, number, number, number];
    } else {
      computedZipBounds = [
        cluster.minLng - MARGIN, 
        cluster.minLat - MARGIN, 
        cluster.maxLng + MARGIN, 
        cluster.maxLat + MARGIN
      ] as [number, number, number, number];
    }
    
    // Set center to the middle of the cluster for better positioning
    const clusterLat = (cluster.minLat + cluster.maxLat) / 2;
    const clusterLon = (cluster.minLng + cluster.maxLng) / 2;
    if (centerLat === undefined || centerLon === undefined) {
      centerLat = clusterLat;
      centerLon = clusterLon;
    }
  }
  // Only calculate radius-based bounds when a radius is explicitly selected
  else if (radiusMiles && radiusMiles > 0 && radiusCenter) {
    // Calculate bounds that encompass the radius circle
    // 1 degree latitude ≈ 69 miles
    const latDelta = radiusMiles / 69;
    // 1 degree longitude varies by latitude, using cos(lat)
    const lngDelta = radiusMiles / (69 * Math.cos(radiusCenter.lat * Math.PI / 180));
    
    computedZipBounds = [
      radiusCenter.lng - lngDelta, // minLng (west)
      radiusCenter.lat - latDelta,  // minLat (south)
      radiusCenter.lng + lngDelta, // maxLng (east)
      radiusCenter.lat + latDelta   // maxLat (north)
    ] as [number, number, number, number];
  }
  else if (activeZip && !computedZipBounds && mapPreceptors.length) {
    const cluster = mapPreceptors.reduce(
      (acc, p) => ({
        minLng: Math.min(acc.minLng, p.longitude as number),
        maxLng: Math.max(acc.maxLng, p.longitude as number),
        minLat: Math.min(acc.minLat, p.latitude as number),
        maxLat: Math.max(acc.maxLat, p.latitude as number),
      }),
      { minLng: Infinity, maxLng: -Infinity, minLat: Infinity, maxLat: -Infinity }
    );
    
    // Add minimum padding for single-point clusters
    const isSinglePoint = cluster.minLng === cluster.maxLng && cluster.minLat === cluster.maxLat;
    if (isSinglePoint) {
      computedZipBounds = [
        cluster.minLng - MIN_BOUNDS_PADDING,
        cluster.minLat - MIN_BOUNDS_PADDING,
        cluster.maxLng + MIN_BOUNDS_PADDING,
        cluster.maxLat + MIN_BOUNDS_PADDING
      ] as [number, number, number, number];
    } else {
      computedZipBounds = [cluster.minLng, cluster.minLat, cluster.maxLng, cluster.maxLat] as [number, number, number, number];
    }
    
    const clusterLat = (cluster.minLat + cluster.maxLat) / 2;
    const clusterLon = (cluster.minLng + cluster.maxLng) / 2;
    if (centerLat === undefined || centerLon === undefined) {
      centerLat = clusterLat;
      centerLon = clusterLon;
    }
  }

  // Only use centerOverride for ZIP searches or when explicitly needed
  // When using bounds-based fitting, leave centerOverride undefined so map can auto-fit properly
  const centerOverride = (activeZip && zipPin)
    ? zipPin
    : (cityParam && cityCenter ? [cityCenter.lon, cityCenter.lat] as [number, number] : undefined);

  return (
    <div className="relative space-y-3">
      {/* Header / controls.
          Desktop (lg+): a custom page header — logo + Home (left), filters (center),
          Dashboard + account (right). The global marketing nav is hidden on desktop
          for this page (see role-aware-header).
          Mobile (<lg): keeps the existing filter button + Contact Us, under the
          marketing header (unchanged). */}
      {/* DESKTOP header — fixed/sticky across the whole page while scrolling */}
      <header ref={desktopHeaderRef} className="hidden lg:flex sticky top-0 z-40 items-center gap-4 h-20 bg-white border-b border-gray-200 px-6">
          {/* Left: logo + Home */}
          <div className="flex items-center gap-6 shrink-0">
            <Link href="/" aria-label="American Clinical Experience — Home">
              <Image src="/images/new logo.png" alt="American Clinical Experience" width={120} height={120} className="h-11 w-auto object-contain" priority />
            </Link>
            <Link href="/" className="text-sm font-medium text-gray-600 hover:text-[var(--brand-primary)] transition-colors">Home</Link>
          </div>

          {/* Center: filters */}
          <div className="flex-1 min-w-0 flex justify-center px-2">
            <div className="w-full max-w-2xl">
              <SimpleFilters
                variant="inline"
                specialtyId={specialtyIdParam}
                state={stateParam || (locationParam.length === 2 ? locationParam : '')}
                city={cityParam}
                preceptors={allPreceptors}
                onFilterChange={handleFilterChange}
              />
            </div>
          </div>

          {/* Right: Contact + Dashboard + account */}
          <div className="flex items-center gap-3 shrink-0">
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[var(--brand-primary)] ring-1 ring-[var(--brand-primary)]/30 transition hover:bg-[var(--brand-primary)] hover:text-white"
              title="Get in touch with the ACE team"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 6-10 7L2 6" />
              </svg>
              <span>Contact Us</span>
            </Link>
            {headerMounted && authLoaded && isSignedIn ? (
              <>
                <Link
                  href={getProfileLink()}
                  className="text-sm px-3 py-2 rounded-md border border-gray-200 text-gray-600 hover:text-[var(--brand-primary)] hover:bg-gray-50 transition-colors"
                >
                  {authUser?.role === 'admin' ? 'Dashboard' : 'My Profile'}
                </Link>
                <UserButton />
              </>
            ) : (
              <Link href="/student/np/login" className="text-sm px-3 py-2 rounded-md bg-[var(--brand-primary)] text-white hover:opacity-90 transition">
                Login
              </Link>
            )}
          </div>
        </header>

      {/* MOBILE controls — pinned to the top; the marketing header scrolls away above it */}
      <div ref={chromeRef} className="lg:hidden sticky top-0 z-30 bg-white">
          <div className="space-y-3 py-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => setFiltersOpen((o) => !o)}
                  aria-expanded={filtersOpen}
                  className="inline-flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:border-gray-300 transition-colors"
                >
                  <span className="inline-flex items-center gap-2 whitespace-nowrap">
                    <svg className="h-4 w-4 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h18M6 12h12M10 19.5h4" />
                    </svg>
                    Filters
                  </span>
                  <svg className={`h-4 w-4 text-gray-500 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>
              </div>

              <Link
                href="/contact"
                className="shrink-0 cursor-pointer inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-[var(--brand-primary)] shadow-sm ring-1 ring-[var(--brand-primary)]/30 transition hover:bg-[var(--brand-primary)] hover:text-white hover:shadow-lg"
                title="Get in touch with the ACE team"
              >
                <span className="hidden sm:flex h-7 w-7 items-center justify-center rounded-full bg-[var(--brand-primary)]/10 text-base">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 6-10 7L2 6" />
                  </svg>
                </span>
                <span>Contact Us</span>
              </Link>
            </div>

            {filtersOpen && (
              <div>
                <SimpleFilters
                  specialtyId={specialtyIdParam}
                  state={stateParam || (locationParam.length === 2 ? locationParam : '')}
                  city={cityParam}
                  preceptors={allPreceptors}
                  onFilterChange={handleFilterChange}
                />
              </div>
            )}
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:px-6" style={{ ['--col-h']: columnHeight } as React.CSSProperties}>
        {/* Left column: card grid — auto height on mobile (avoids a big gap with
            few results), fixed two-pane height on desktop. */}
        <div className="flex flex-col lg:h-[var(--col-h)]">
          <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600">
              <p className="text-blue-100 text-sm font-medium">Results</p>
              <div className="flex items-center gap-2 text-white">
                <span className="text-sm font-bold">{viewportFiltered.length} in view</span>
                {filtered.length !== viewportFiltered.length && (
                  <span className="text-blue-100 text-sm">({filtered.length} total)</span>
                )}
              </div>
            </div>

            {/* Badges */}
            {(locationBadgeLabel || specialtyIdParam || (!specialtyIdParam && specialtyParam)) && (
              <div className="px-4 py-2 flex flex-wrap items-center gap-2">
                {locationBadgeLabel && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                    {locationBadgeLabel}{locationBadgeSuffix}{locationBadgeApprox}
                  </span>
                )}
                {specialtyIdParam && (() => {
                  const specialtyId = Number(specialtyIdParam);
                  const specialty = allPreceptors.find(p => p.specialty_ids?.includes(specialtyId))?.specialties?.find(s => Number(s.id) === specialtyId);
                  return specialty ? (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200">{specialty.name}</span>
                  ) : null;
                })()}
                {!specialtyIdParam && specialtyParam && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200">{specialtyParam}</span>
                )}
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5 lg:flex-1 lg:overflow-auto pr-1 custom-scroll">
            {isLoadingPreceptors ? (
              <div className="text-sm text-[var(--brand-muted)] p-4 border rounded-md col-span-full text-center">
                <div className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 border-2 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin"></div>
                  Loading preceptors...
                </div>
              </div>
            ) : preceptorError ? (
              <div className="text-sm text-[var(--brand-muted)] p-4 border rounded-md col-span-full bg-orange-50">
                <div className="font-semibold text-orange-900">Failed to load preceptors</div>
                <div className="text-xs text-orange-700 mt-1">{preceptorError}</div>
              </div>
            ) : viewportFiltered.length ? (
              listPreceptors.map((p, idx) => (
                <div key={p.id} onClick={() => setSelectedId(p.id)}>
                  <PreceptorResultCard preceptor={p} index={idx} selected={p.id === selectedId} selectedSpecialtyId={specialtyIdParam ? Number(specialtyIdParam) : undefined} track={trackKey} />
                </div>
              ))
            ) : (
              <div className="text-sm text-[var(--brand-muted)] p-4 border rounded-md col-span-full">
                {filtered.length > 0 
                  ? "No preceptors visible in current map view. Zoom out or pan to see more."
                  : "No rotations found."}
              </div>
            )}
          </div>
          {viewportFiltered.length > visibleCount && (
            <div className="pt-3">
              <button type="button" onClick={() => setVisibleCount(c => c + 20)} className="text-sm px-3 py-1.5 rounded-md border border-[var(--brand-border)] bg-white hover:bg-neutral-50 font-medium">
                Show More ({visibleCount}/{viewportFiltered.length})
              </button>
            </div>
          )}
        </div>
        {/* Right column: map — fixed viewport-relative height on mobile, two-pane height on desktop. */}
        <div className="flex flex-col h-[60vh] lg:h-[var(--col-h)]">
          <div className="lg:sticky lg:top-4 h-full rounded-xl border border-[var(--brand-border)] shadow-sm overflow-hidden bg-white">
            <RotationMap
              showIndices={false}
              preceptors={mapPreceptors.map(p => ({ id: p.id, latitude: p.latitude, longitude: p.longitude, rotationCode: p.rotationCode, name: p.name, specialty: p.specialty, city: p.city, zip: p.zip, photoUrl: p.photoUrl }))}
              zoom={showingDefaultLocation ? MAP_ZOOM_USA : MAP_ZOOM_FILTERED}
              markerColor="#2563eb"
              losAngelesOnly={false}
              zip={activeZip || undefined}
              pinStyle="pin"
              lockZoom={false}
              fixedZoomLevel={undefined}
              autoFit={true}
              freezeZoom={false}
              restrictZoomOut={false}
              disablePan={false}
              disableUserZoom={false}
              zipFitPadding={12}
              detailView
              userLocation={undefined}
              centerOverride={centerOverride}
              zipBounds={computedZipBounds}
              onMarkerClick={(id) => setSelectedId(id)}
              selectedId={selectedId || undefined}
              fullHeight
              // Disable centroid clustering so individual markers and ZIP pin always show
              clusterCentroidMode={false}
              clusterLabel={undefined}
              radiusMiles={isImg ? 0.6 : 0.24} // Obscure exact location; IMG uses a larger circle than NP/PA
              onBoundsChange={handleMapBoundsChange} // Always sync list with visible map area
              fitBoundsKey={fitBoundsKey} // Force re-fit when filter is re-selected
            />
          </div>
        </div>
      </div>

      {/* Deal Booking Modal */}
      <DealBookingModal
        isOpen={isMessageModalOpen}
        onClose={() => {
          setIsMessageModalOpen(false);
          setSelectedPreceptorForMessage(null);
        }}
        preceptorName={selectedPreceptorForMessage?.name}
        preceptorSpecialty={selectedPreceptorForMessage?.specialty}
        preceptorCity={selectedPreceptorForMessage?.city}
        onSubmit={(formData: DealBookingFormData) => {
          console.log('📧 Message submitted:', {
            preceptor: selectedPreceptorForMessage?.name,
            formData,
          });
          // TODO: Send to backend API
        }}
      />
    </div>
  );
};

export default PreceptorSearchExperience;
